import React, { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import PageMeta from '../../components/common/PageMeta';
import { useAuth } from '../../context/AuthContext';
import {
  markGroupCheckoutStarted,
  markStoreCheckoutStarted,
} from '../../hooks/useGroupCheckoutRefresh';
import {
  type AddonSelection,
  NO_ADDONS,
  addonLines,
  addonPayload,
  formatMinutes,
  formatMoney,
  formatRate,
  includedSelection,
  monthlyTotalCents,
} from '../../shared/plan';
import {
  Button,
  Callout,
  IconArrowRight,
  IconChevronLeft,
  IconExternal,
  Page,
  PlanChooser,
  type PlanOption,
  PlanName,
  QueryGate,
  Reason,
  Receipt,
  type ReceiptLine,
  ReceiptLines,
  ReceiptTotal,
  SUPPORT_EMAIL,
  SectionHead,
  Skeleton,
  StatusLabel,
  Surface,
  Tag,
} from '../../ui/v2';
import { CheckoutPreview } from './CheckoutPreview';
import {
  type BillingScope,
  loadPreview,
  submitPlan,
  toGate,
  useInvalidateBilling,
  usePlans,
  usePortal,
  useSubscription,
} from './data';
import {
  BILLING_PATH,
  PLAN_TAG,
  type Plan,
  type Subscription,
  activeAddons,
  formatDate,
  groupBillingPath,
  isAwaitingBillingStart,
  isLive,
  pricingFromPlan,
  pricingFromSubscription,
  subscriptionWord,
} from './model';

/**
 * Plans V2 — choose or change a plan, for a store or a store group.
 *
 * Left: `PlanChooser`, a list where every plan's numbers line up in
 * columns; the selected row opens for add-ons and features. Right: the
 * `Receipt`, which owns the one primary. Without a live subscription the
 * primary goes to Stripe Checkout (setup fee + first month due today);
 * with one, it opens `CheckoutPreview` and the change happens in place.
 *
 * Add-ons can be chosen only for a new checkout: an in-place change keeps
 * the subscription's add-ons, so on a live plan they're shown locked.
 */
const PlansPage: React.FC<{ scope?: 'store' | 'group' }> = ({
  scope = 'store',
}) => (scope === 'group' ? <GroupPlans /> : <StorePlans />);

export default PlansPage;

const STORE_SCOPE: BillingScope = { kind: 'store' };
const CUSTOM_CODE = '__custom__';

const PlansFrame: React.FC<{
  title: string;
  meta?: React.ReactNode;
  back: { to: string; label: string };
  children: React.ReactNode;
}> = ({ title, meta, back, children }) => (
  <Page fill title="Plans" meta={meta}>
    <PageMeta title={title} description="Choose or change your plan" />
    <Surface className="flex min-h-0 flex-1 flex-col">
      <div className="v2-scroll min-h-0 flex-1 overflow-y-auto">
        <div className="max-w-[1160px] px-4 pb-16 pt-4 lg:px-6">
          <Link
            to={back.to}
            className="-ml-1 inline-flex h-7 items-center gap-1 rounded-v2-sm px-1 text-v2-12 text-v2-fg-3 hover:bg-v2-sunken hover:text-v2-fg"
          >
            <IconChevronLeft size={12} /> {back.label}
          </Link>
          {children}
        </div>
      </div>
    </Surface>
  </Page>
);

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

const StorePlans: React.FC = () => {
  const { user, loading, userInfoSettled } = useAuth();
  // Fail closed: wait for authoritative user info before offering plans.
  const settled = !loading && userInfoSettled;
  const managed = settled && user?.billing_managed_at_group_level === true;
  const ready = settled && !managed;
  const plansQ = usePlans(STORE_SCOPE, ready);
  const subQ = useSubscription(STORE_SCOPE, ready);

  const groupId = user?.store_group_id ?? null;
  const groupPlansHref =
    user?.can_manage_group_billing && groupId != null
      ? `${groupBillingPath(groupId)}/plans`
      : null;

  return (
    <PlansFrame
      title="AskArthur | Plans"
      meta={
        user?.store_name ? (
          <span className="hidden sm:inline">{user.store_name}</span>
        ) : undefined
      }
      back={{ to: BILLING_PATH, label: 'Billing' }}
    >
      {!settled ? (
        <ChooserSkeleton />
      ) : managed ? (
        <ManagedNotice
          groupName={user?.store_group_name ?? 'your store group'}
          groupHref={groupPlansHref}
        />
      ) : (
        <QueryGate
          query={toGate(plansQ)}
          errorTitle="Plans could not be loaded."
          skeleton={<ChooserSkeleton />}
        >
          {(plans) => (
            <QueryGate
              query={toGate(subQ)}
              errorTitle="Your subscription could not be loaded."
              skeleton={<ChooserSkeleton />}
            >
              {(sub) => (
                <Chooser
                  scope={STORE_SCOPE}
                  plans={plans}
                  subscription={sub}
                  billingHref={BILLING_PATH}
                  group={false}
                />
              )}
            </QueryGate>
          )}
        </QueryGate>
      )}
    </PlansFrame>
  );
};

// ---------------------------------------------------------------------------
// Group (access is checked by `GroupBillingRoute`)
// ---------------------------------------------------------------------------

const GroupPlans: React.FC = () => {
  const { groupId = '' } = useParams();
  const scope = useMemo<BillingScope>(
    () => ({ kind: 'group', groupId }),
    [groupId],
  );
  const { user } = useAuth();
  const name =
    user?.manageable_billing_groups?.find((g) => String(g.id) === groupId)
      ?.name ?? 'Store group';
  const plansQ = usePlans(scope);
  const subQ = useSubscription(scope);

  return (
    <PlansFrame
      title={`AskArthur | ${name} plans`}
      meta={<span className="hidden sm:inline">{name} · group</span>}
      back={{ to: groupBillingPath(groupId), label: 'Group billing' }}
    >
      <QueryGate
        query={toGate(plansQ)}
        errorTitle="Plans could not be loaded."
        skeleton={<ChooserSkeleton />}
      >
        {(plans) => (
          <QueryGate
            query={toGate(subQ)}
            errorTitle="The shared subscription could not be loaded."
            skeleton={<ChooserSkeleton />}
          >
            {(sub) => (
              <Chooser
                scope={scope}
                plans={plans}
                subscription={sub}
                billingHref={groupBillingPath(groupId)}
                group
              />
            )}
          </QueryGate>
        )}
      </QueryGate>
    </PlansFrame>
  );
};

// ---------------------------------------------------------------------------
// The chooser + receipt, shared by both scopes
// ---------------------------------------------------------------------------

const Chooser: React.FC<{
  scope: BillingScope;
  plans: Plan[];
  subscription: Subscription | null;
  billingHref: string;
  group: boolean;
}> = ({ scope, plans, subscription: sub, billingHref, group }) => {
  const navigate = useNavigate();
  const invalidate = useInvalidateBilling();
  const portal = usePortal(scope);
  // Stripe is billing now, so a change happens in place. A plan whose
  // activation fee is paid but whose Stripe subscription has not started
  // is still the current plan: a new checkout would charge it again.
  const inPlace = isLive(sub);
  const awaitingStart = isAwaitingBillingStart(sub);
  const currentSub = inPlace || awaitingStart ? sub : null;
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const currency = sub?.currency ?? plans[0]?.currency ?? 'USD';
  const custom = !!currentSub && currentSub.pricing_source === 'custom';
  const currentCode = currentSub
    ? custom
      ? CUSTOM_CODE
      : (currentSub.plan?.code ?? null)
    : null;
  const scheduledCode = currentSub?.pending_plan?.code ?? null;
  const lockedReason = currentSub
    ? `Add-ons stay as they are when the plan changes. To change them, email ${SUPPORT_EMAIL}.`
    : undefined;

  const options: PlanOption[] = useMemo(() => {
    const list: PlanOption[] = plans.map((p) => ({
      code: p.code,
      name: p.name,
      description: p.description,
      pricing: pricingFromPlan(p),
      features: p.feature_points,
      addonsLocked: lockedReason,
      tag:
        p.code === currentCode
          ? PLAN_TAG.current
          : p.code === scheduledCode
            ? PLAN_TAG.scheduled
            : undefined,
    }));
    if (custom && currentSub) {
      list.unshift({
        code: CUSTOM_CODE,
        name: currentSub.plan?.name
          ? `${currentSub.plan.name} — your pricing`
          : 'Your agreed pricing',
        description:
          'Set for you by AskArthur. Catalog plans below replace it.',
        pricing: pricingFromSubscription(currentSub),
        hue: 'teal',
        tag: PLAN_TAG.custom,
        addonsLocked:
          'Add-ons on agreed pricing are set by your account manager.',
      });
    }
    return list;
  }, [plans, currentCode, scheduledCode, custom, currentSub, lockedReason]);

  const [selected, setSelected] = useState<string | null>(
    currentCode ?? options[0]?.code ?? null,
  );
  const [addons, setAddons] = useState<AddonSelection>(() =>
    currentSub
      ? activeAddons(currentSub)
      : options[0]
        ? includedSelection(options[0].pricing)
        : NO_ADDONS,
  );
  const option = options.find((o) => o.code === selected) ?? null;

  const choose = (code: string) => {
    setSelected(code);
    if (currentSub) return;
    const o = options.find((x) => x.code === code);
    if (o) setAddons(includedSelection(o.pricing));
  };

  const [preview, setPreview] = useState(false);
  const [checkoutBusy, setCheckoutBusy] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  const pricing = option?.pricing ?? null;
  const monthly = pricing ? monthlyTotalCents(pricing, addons) : 0;

  let primaryLabel = 'Choose a plan';
  let disabledReason: string | undefined;
  if (!option) disabledReason = 'Pick a plan above.';
  else if (!currentSub) primaryLabel = 'Continue to payment';
  else if (awaitingStart) {
    primaryLabel =
      option.code === currentCode ? 'Current plan' : `Change to ${option.name}`;
    disabledReason =
      'Billing hasn’t started for this plan, so it can’t be changed yet.';
  } else if (currentSub.status === 'PAST_DUE') {
    primaryLabel = `Change to ${option.name}`;
    disabledReason = 'Settle the open invoice before changing plans.';
  } else if (option.code === scheduledCode) {
    primaryLabel = `Change to ${option.name}`;
    disabledReason = `${option.name} is already scheduled for ${currentSub.current_period_end ? formatDate(currentSub.current_period_end, tz) : 'the next period'}.`;
  } else if (option.code === currentCode) {
    primaryLabel = 'Current plan';
    disabledReason = 'This is your current plan. Pick another plan to change.';
  } else primaryLabel = `Change to ${option.name}`;

  const planCode = option?.code ?? '';
  const body = useMemo(
    () =>
      pricing
        ? { plan_code: planCode, ...addonPayload(pricing, addons) }
        : { plan_code: planCode },
    [planCode, pricing, addons],
  );

  const afterSubmit = useCallback(
    async (res: { checkout_url?: string }) => {
      if (res.checkout_url) {
        if (scope.kind === 'group') markGroupCheckoutStarted(scope.groupId);
        else markStoreCheckoutStarted();
        window.location.href = res.checkout_url;
        return;
      }
      await invalidate();
      navigate(`${billingHref}?updated=1`);
    },
    [scope, invalidate, navigate, billingHref],
  );

  const startCheckout = async () => {
    setCheckoutBusy(true);
    setCheckoutError(null);
    try {
      await afterSubmit(await submitPlan(scope, body));
    } catch (e) {
      setCheckoutError((e as Error).message);
      setCheckoutBusy(false);
    }
  };

  const previewFor = useCallback(
    () => loadPreview(scope, planCode),
    [scope, planCode],
  );
  const confirm = useCallback(
    async () => afterSubmit(await submitPlan(scope, body)),
    [scope, body, afterSubmit],
  );

  const receiptLines: ReceiptLine[] =
    option && pricing
      ? [
          {
            key: 'base',
            label: `${option.name} base`,
            value: `${formatMoney(pricing.baseCents, currency)}/mo`,
          },
          ...addonLines(pricing, addons)
            .filter((a) => a.active)
            .map((a) => ({
              key: a.key,
              label: a.label,
              value: a.included
                ? 'Included'
                : `${formatMoney(a.cents, currency)}/mo`,
              muted: a.included,
            })),
        ]
      : [];
  const dueToday = pricing ? monthly + pricing.setupFeeCents : 0;
  const word = subscriptionWord(sub, tz);

  return (
    <div className="mt-4 grid gap-10 lg:grid-cols-[minmax(0,1fr)_320px]">
      <div className="min-w-0">
        <SectionHead
          title={
            awaitingStart
              ? 'Your plan'
              : currentSub
                ? 'Change plan'
                : group
                  ? 'Choose a shared plan'
                  : 'Choose a plan'
          }
          description={
            awaitingStart
              ? 'This plan is already set. Billing hasn’t started, so changing it here would start a second checkout.'
              : group
                ? 'One plan for every store in the group; minutes are pooled and overage is split by use.'
                : currentSub
                  ? 'Upgrades take effect today, prorated. Downgrades start at the next period so you keep the minutes you paid for.'
                  : 'Every plan includes the AskArthur phone agent, the dashboard and call recordings. Pick by how many minutes you expect each month.'
          }
          action={
            sub ? (
              <span className="flex items-center gap-2">
                {sub.plan && (
                  <PlanName
                    code={custom ? null : sub.plan.code}
                    name={sub.plan.name}
                    size="sm"
                    hue={custom ? 'teal' : undefined}
                  />
                )}
                <StatusLabel size="sm" tone={word.tone} hollow={word.hollow}>
                  {word.label}
                </StatusLabel>
              </span>
            ) : undefined
          }
        />
        {currentSub?.pending_plan && (
          <Callout tone="accent" className="mt-3">
            <span className="font-medium text-v2-fg">
              {currentSub.pending_plan.name}
            </span>{' '}
            is scheduled for{' '}
            {currentSub.current_period_end
              ? formatDate(currentSub.current_period_end, tz)
              : 'the next period'}
            . Choosing a different plan replaces that change.
          </Callout>
        )}
        {inPlace && currentSub?.status === 'PAST_DUE' && (
          <Callout
            tone="danger"
            className="mt-3"
            title="Plans are locked while an invoice is unpaid."
          >
            <Link to={billingHref} className="underline">
              Update the payment method on Billing
            </Link>
            , then come back.
          </Callout>
        )}
        <PlanChooser
          className="mt-4"
          options={options}
          value={selected}
          onChange={choose}
          addons={addons}
          onAddonsChange={setAddons}
          aria-label={group ? 'Shared plans' : 'Plans'}
        />
        {custom && option && option.code !== CUSTOM_CODE && (
          <Reason className="mt-3">
            Switching to a catalog plan replaces your agreed pricing. Your
            account manager can restore it later.
          </Reason>
        )}
        <Reason className="mt-3">
          Prices exclude tax. Minutes count from when Arthur answers to when the
          call ends, rounded up to the next minute
          {pricing && pricing.perOrderCents > 0
            ? `; confirmed orders are ${formatRate(pricing.perOrderCents, currency, 'order')}`
            : ''}
          .
        </Reason>
      </div>

      <Receipt
        sticky
        heading={
          <div className="text-v2-12 font-semibold uppercase tracking-[0.04em] text-v2-fg-3">
            {inPlace ? 'Your new plan' : 'Your plan'}
          </div>
        }
        aria-label="Plan summary"
      >
        {option && pricing ? (
          <>
            <div className="mb-3 flex items-center justify-between gap-3">
              <PlanName
                code={option.code === CUSTOM_CODE ? null : option.code}
                name={option.name}
                hue={option.hue}
              />
              {option.tag && (
                <Tag tone={option.tag.tone}>{option.tag.label}</Tag>
              )}
            </div>
            <ReceiptLines lines={receiptLines} />
            <ReceiptTotal
              className="mt-1"
              label="Monthly total"
              value={formatMoney(monthly, currency)}
              note={`${formatMinutes(pricing.includedMinutes)} included${group ? ', shared' : ''}, then ${formatRate(pricing.overageCentsPerMinute, currency, 'min')}.`}
            />
            {!currentSub && (
              <div className="mt-4 border-t border-v2-line pt-3">
                <ReceiptLines
                  lines={[
                    {
                      key: 'setup',
                      label: 'Setup fee',
                      sub: 'one time',
                      value: formatMoney(pricing.setupFeeCents, currency),
                      muted: pricing.setupFeeCents === 0,
                    },
                    {
                      key: 'first',
                      label: 'First month',
                      value: formatMoney(monthly, currency),
                    },
                  ]}
                />
                <div className="mt-2 flex items-baseline justify-between">
                  <span className="text-v2-13 font-semibold">Due today</span>
                  <span className="v2-num text-v2-16 font-semibold">
                    {formatMoney(dueToday, currency, { exact: true })}
                  </span>
                </div>
              </div>
            )}
            <div className="mt-5">
              <Button
                variant="primary"
                className="h-9 w-full"
                loading={checkoutBusy}
                disabledReason={disabledReason}
                onClick={() =>
                  inPlace ? setPreview(true) : void startCheckout()
                }
                trailing={!currentSub ? <IconExternal size={12} /> : undefined}
              >
                {primaryLabel}
              </Button>
              {disabledReason && (
                <Reason className="mt-2">{disabledReason}</Reason>
              )}
              {!currentSub && !disabledReason && (
                <p className="mt-2 text-v2-12 leading-[18px] text-v2-fg-3">
                  Card details are entered on Stripe’s secure checkout. Arthur
                  starts answering as soon as payment clears.
                </p>
              )}
              {checkoutError && (
                <Callout tone="danger" className="mt-3">
                  {checkoutError}
                </Callout>
              )}
            </div>
          </>
        ) : (
          <Reason>Pick a plan to see the total.</Reason>
        )}
      </Receipt>

      {inPlace &&
        currentSub &&
        option &&
        pricing &&
        option.code !== currentCode && (
          <CheckoutPreview
            open={preview}
            onClose={() => setPreview(false)}
            current={
              custom
                ? {
                    code: null,
                    name: currentSub.plan?.name ?? 'your agreed pricing',
                  }
                : currentSub.plan
            }
            next={{ code: option.code, name: option.name, pricing }}
            addons={addons}
            currency={currency}
            tz={tz}
            loadPreview={previewFor}
            confirm={confirm}
            onOpenPortal={portal.open}
            portalBusy={portal.busy}
          />
        )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Small pieces
// ---------------------------------------------------------------------------

const ManagedNotice: React.FC<{
  groupName: string;
  groupHref: string | null;
}> = ({ groupName, groupHref }) => (
  <div className="mt-4 max-w-[640px]">
    <Callout
      tone="accent"
      title={`This store is on ${groupName}’s shared plan.`}
    >
      Plans are chosen once for the whole group.
      {groupHref ? (
        <div className="mt-2">
          <Link to={groupHref}>
            <Button
              size="sm"
              variant="secondary"
              trailing={<IconArrowRight size={12} />}
            >
              Group plans
            </Button>
          </Link>
        </div>
      ) : (
        <div className="mt-1 text-v2-fg-3">
          Ask your group administrator to change it.
        </div>
      )}
    </Callout>
  </div>
);

const ChooserSkeleton: React.FC = () => (
  <div
    className="mt-4 grid gap-10 lg:grid-cols-[minmax(0,1fr)_320px]"
    aria-busy
  >
    <div>
      <Skeleton className="h-4 w-40" />
      <Skeleton className="mt-2 block h-3 w-80" />
      <div className="mt-4 border-y border-v2-line">
        {Array.from({ length: 3 }).map((_, i) => (
          <div
            key={i}
            className="flex h-14 items-center gap-4 border-b border-v2-line px-3 last:border-b-0"
          >
            <Skeleton className="h-2.5 w-2.5" />
            <Skeleton className="h-3.5 w-24" />
            <Skeleton className="ml-auto h-3 w-48" />
          </div>
        ))}
      </div>
    </div>
    <div className="rounded-v2-lg border border-v2-line p-5">
      <Skeleton className="h-3 w-20" />
      <Skeleton className="mt-4 block h-4 w-32" />
      <Skeleton className="mt-3 block h-3 w-full" />
      <Skeleton className="mt-2 block h-3 w-full" />
      <Skeleton className="mt-6 block h-9 w-full" />
    </div>
  </div>
);
